// ── Jobs Library ──────────────────────────────────────────────────────────────

// Fields to search in requisition-number view
export const REQ_SEARCHABLE_FIELDS = [
  "sb_requisition_number",
  "job_code",
  "job_title_for_jd",
  "legal_entity",
  "business_unit",
  "role_type",
  "location",
  "function",
  "sub_function",
  "job_level",
  "version_info",
];

// Fields to search in job-code view (parent-level keys only)
export const JOB_CODE_SEARCHABLE_FIELDS = [
  "job_code",
  "job_title",
  "business_unit",
  "role_type",
  "job_level",
  "legal_entity",
];

export const FILTER_KEYS = [
  "sb_requisition_number",
  "job_code",
  "job_title",
  "legal_entity",
  "business_unit",
  "role_type",
  "location",
  "function",
  "sub_function",
  "job_level",
  "version_info",
];

export const VIEW_MODES = {
  JOB_CODE:          'job-code',
  REQUISITION_NUMBER: 'requisition-number',
};

// ── JD Status ─────────────────────────────────────────────────────────────────

export const JD_STATUSES = {
  PENDING_ANALYSIS: 'PENDING_ANALYSIS',
  NEW:              'NEW',
  // Saved privately by its owner: every Revise result (source GENERATED) and, later, Save as Draft
  // (source HUMAN-EDITED) (Save as Draft design, section 4)
  DRAFT:            'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  PUBLISHED:        'PUBLISHED',
  UNPUBLISHED:      'UNPUBLISHED',
  NEEDS_REVISION:   'NEEDS_REVISION',
  GENERATED:        'GENERATED',
  REGENERATED:      'REGENERATED',
  LOCKED:           'LOCKED',
  NEEDS_WORK:       'NEEDS_WORK',
  GENERATION_FAILED: 'GENERATION_FAILED',
  // An older version left in a temporary status (New, Draft, Pending Approval, Needs Revision) when
  // the JD moved on (Status and Source design, section 4.3). Permanent, never the latest version, so
  // never a Jobs Library list status; the version dropdown shows its source only.
  HISTORICAL:       'HISTORICAL',
};

// Statuses that allow editing a JD (NEW: a brand-new v1.00 the AI wrote, editable like GENERATED;
// DRAFT: the owner's latest draft, edited or revised again)
export const JD_STATUSES_EDITABLE = [
  JD_STATUSES.NEW,
  JD_STATUSES.DRAFT,
  JD_STATUSES.GENERATED,
  JD_STATUSES.REGENERATED,
  JD_STATUSES.UNPUBLISHED,
];

// ── JD Source (jd_input_type) ─────────────────────────────────────────────────
// Who wrote a version (Status and Source design, section 4.2). Only these two values are written.
export const JD_SOURCES = {
  GENERATED:    'GENERATED',
  HUMAN_EDITED: 'HUMAN-EDITED',
};

// Older data still holds the detailed AI values until the backfill ('human-edit' for a person's
// version): read only, never sent
const LEGACY_AI_INPUT_TYPES = ['create', 'upload', 'system_upload', 'regenerate'];
const LEGACY_HUMAN_INPUT_TYPES = ['human-edit'];

// How a source shows on screen (as dev today): AI-GENERATED or HUMAN-EDITED
export const JD_SOURCE_LABELS = {
  [JD_SOURCES.GENERATED]:    'AI-GENERATED',
  [JD_SOURCES.HUMAN_EDITED]: 'HUMAN-EDITED',
};

// The on-screen source of a stored jd_input_type, new or old value ('' when missing or unknown).
// Same mapping as the backend's parse_jd_source (app/enums/jd_source.py).
export const getJdSourceLabel = (jdInputType) => {
  const value = typeof jdInputType === 'string' ? jdInputType.trim() : '';
  if (value.toUpperCase() === JD_SOURCES.GENERATED || LEGACY_AI_INPUT_TYPES.includes(value.toLowerCase())) {
    return JD_SOURCE_LABELS[JD_SOURCES.GENERATED];
  }
  if (value.toUpperCase() === JD_SOURCES.HUMAN_EDITED || LEGACY_HUMAN_INPUT_TYPES.includes(value.toLowerCase())) {
    return JD_SOURCE_LABELS[JD_SOURCES.HUMAN_EDITED];
  }
  return '';
};

// Which AI flow wrote a version. The source alone no longer says, so the server sends jd_ai_flow;
// a response without it still names the flow in jd_input_type (old values).
export const JD_AI_FLOWS = {
  CREATE:        'create',
  REVISE:        'upload',
  SYSTEM_UPLOAD: 'system_upload',
  REGENERATE:    'regenerate',
};

export const getJdAiFlow = (jd) => {
  if (jd?.jd_ai_flow !== undefined) return jd.jd_ai_flow;
  const value = typeof jd?.jd_input_type === 'string' ? jd.jd_input_type.trim().toLowerCase() : '';
  return LEGACY_AI_INPUT_TYPES.includes(value) ? value : null;
};

// A version "has manual edits" when a person wrote it (source HUMAN-EDITED, old or new value). Revise
// on such a version warns first; a version never edited by hand does not.
export const hasManualEdits = (jdInputType) =>
  getJdSourceLabel(jdInputType) === JD_SOURCE_LABELS[JD_SOURCES.HUMAN_EDITED];

// The evaluation score (online-user JD page, the only page that shows one): only an AI-generated version has one;
// a version a person wrote (source HUMAN-EDITED) never gets a score, and shows this line instead
export const EVAL_SCORE_AI_ONLY_NOTE = 'Evaluation scores are only available for AI-generated versions.';
export const isScoredSource = (jdInputType) => !hasManualEdits(jdInputType);

// The status a version's content is read as: a DRAFT as its source (a Revise result like GENERATED, a
// person's draft like HUMAN-EDITED), every other status as it is. Same rule as the backend's
// jd_content_status (app/enums/jd_source.py).
export const getJdContentStatus = (jd) => {
  const status = jd?.jd_status;
  if (status !== JD_STATUSES.DRAFT) return status;
  return hasManualEdits(jd?.jd_input_type) ? JD_SOURCES.HUMAN_EDITED : JD_SOURCES.GENERATED;
};

// The source fields of a publish payload: a publish with changes is a person's new version; a publish
// without changes sends no source, so the server keeps the version's stored one.
export const publishSourceFields = (isContentChanged) =>
  (isContentChanged ? { jd_input_type: JD_SOURCES.HUMAN_EDITED } : {});

// PRD §5.1 — tooltip content strings for a JD's status. Shared by Jobs Library
// and Compensation Management so both surfaces describe each status identically.
export const TOOLTIP_STATUS_PROCESSING = "This JD is currently being generated by AI. This typically takes 4-5 minutes. You cannot open it until generation is complete.";
export const TOOLTIP_STATUS_QUEUED = "This JD is waiting or about to start generating. Its status will update automatically.";
// New, Draft and Historical: the wireframe's wording (jobs-library-ownership-v3.html, STATUS_META)
export const TOOLTIP_STATUS_NEW = "A brand-new JD (created or uploaded) that has not been edited, sent for approval or published yet.";
export const TOOLTIP_STATUS_DRAFT = "Saved privately by its owner. Everyone else sees the latest non-draft version.";
export const TOOLTIP_STATUS_UNPUBLISHED = "This JD has been generated but not yet published. It can be edited, revised, or sent for approval.";
export const TOOLTIP_STATUS_PENDING_APPROVAL = "This JD has been submitted for review. The assigned approver will review and either publish or request revisions.";
export const TOOLTIP_STATUS_NEEDS_REVISION = "The approver has requested changes to this JD. The creator needs to make the requested edits and resubmit.";
export const TOOLTIP_STATUS_PUBLISHED = "This JD is live and available for use across SkillsBridge. Download as PDF or DOCX from the Job Details page.";
export const TOOLTIP_STATUS_PROCESSING_FAILED = "JD generation failed. Use the Retry button to try again, or contact your administrator if the issue persists.";
export const TOOLTIP_STATUS_HISTORICAL = "An older version left behind when the JD moved on: an older New or Draft version when a newer one is sent or published, the Needs Revision version at resubmit, or a Pending Approval version replaced by a newer one. Permanent; never the latest version.";

// Revise (JD page). Over a draft the tooltip says what happens to it (Save as Draft design, section 4,
// "Button rules"); HR Managers, who can publish, are told so.
export const TOOLTIP_REVISE = "Ask the AI to regenerate this JD using updated inputs. Creates a new version. Takes approximately 4–5 minutes.";
export const TOOLTIP_REVISE_OVER_DRAFT = "Ask the AI to regenerate this JD using updated inputs. Creates a new version. Takes approximately 4-5 minutes. If your latest version is a Revise draft, it is replaced instead. Your drafts stay private to you and the people with access until you send them for approval.";
export const TOOLTIP_REVISE_OVER_DRAFT_MANAGER = "Ask the AI to regenerate this JD using updated inputs. Creates a new version. Takes approximately 4-5 minutes. If your latest version is a Revise draft, it is replaced instead. Your drafts stay private to you and the people with access until you send them for approval or publish them.";

export const getReviseTooltip = (latestStatus, canPublish = false) => {
  if (latestStatus !== JD_STATUSES.DRAFT) return TOOLTIP_REVISE;
  return canPublish ? TOOLTIP_REVISE_OVER_DRAFT_MANAGER : TOOLTIP_REVISE_OVER_DRAFT;
};

// The popup shown before revising a version with manual edits (wireframe: "Revise this version?",
// Cancel / Revise anyway). Close and Esc do nothing: only the two buttons answer it.
export const REVISE_MANUAL_EDITS_WARNING = {
  title: 'Revise this version?',
  content: 'This version includes manual edits. Revising it may replace them with AI-generated content. To keep your edits, first copy them before revising.',
  okText: 'Revise anyway',
  cancelText: 'Cancel',
  closable: false,
  keyboard: false,
  maskClosable: false,
};

// Opens Revise, after the manual-edits warning when the version being revised has manual edits.
// `confirm` is the popup opener (antd Modal.confirm); `openRevise` opens the Revise drawer.
export const confirmReviseIfManuallyEdited = (jdInputType, openRevise, confirm) => {
  if (!hasManualEdits(jdInputType)) {
    openRevise();
    return;
  }
  confirm({ ...REVISE_MANUAL_EDITS_WARNING, onOk: openRevise });
};

// Maps an underlying jd_status to the appropriate PRD tooltip content.
export const getStatusTooltip = (status) => {
  switch (status) {
    case JD_STATUSES.PUBLISHED:
      return TOOLTIP_STATUS_PUBLISHED;
    case JD_STATUSES.PENDING_APPROVAL:
      return TOOLTIP_STATUS_PENDING_APPROVAL;
    case JD_STATUSES.NEEDS_REVISION:
      return TOOLTIP_STATUS_NEEDS_REVISION;
    case JD_STATUSES.PENDING_ANALYSIS:
      return TOOLTIP_STATUS_PROCESSING;
    case JD_STATUSES.GENERATION_FAILED:
      return TOOLTIP_STATUS_PROCESSING_FAILED;
    case JD_STATUSES.NEW:
      return TOOLTIP_STATUS_NEW;
    case JD_STATUSES.DRAFT:
      return TOOLTIP_STATUS_DRAFT;
    case JD_STATUSES.HISTORICAL:
      return TOOLTIP_STATUS_HISTORICAL;
    case JD_STATUSES.UNPUBLISHED:
    default:
      return TOOLTIP_STATUS_UNPUBLISHED;
  }
};

// A JD sits in PENDING_ANALYSIS from insert until its worker finishes, and a bulk
// upload queues up to 50 at once, drained a few at a time. Worker errors set
// GENERATION_FAILED; only a killed worker leaves a JD stuck here, so treat
// PENDING_ANALYSIS as failed (retryable) only once it is this old.
export const STALE_PENDING_ANALYSIS_MS = 2 * 60 * 60 * 1000;
// A queued JD can wait behind a long queue; only a task that never reached the worker waits this long.
export const STALE_QUEUED_JD_MS = 6 * 60 * 60 * 1000;
export const PENDING_JD_REFRESH_MS = 30 * 1000;

// requisition_view tags each PENDING_ANALYSIS row QUEUED or PROCESSING from the worker's task stream.
export const isJdQueued = (record) =>
  record?.jd_status === JD_STATUSES.PENDING_ANALYSIS && record?.generation_state === 'QUEUED';

export const isJdGenerationFailed = (record, now = Date.now()) => {
  if (record?.jd_status === JD_STATUSES.GENERATION_FAILED) return true;
  if (record?.jd_status !== JD_STATUSES.PENDING_ANALYSIS) return false;
  // Time since the worker's last event, else since the row changed.
  // requisition_view sends epoch ms (number or numeric string); accept an ISO string too.
  // No timestamp means we can't tell, so keep showing it as processing.
  const ts = record.generation_updated_at ?? record.last_updated_time_stamp;
  if (ts === null || ts === undefined || ts === "") return false;
  const updatedAt = Number.isFinite(Number(ts)) ? Number(ts) : Date.parse(ts);
  const staleAfter = isJdQueued(record) ? STALE_QUEUED_JD_MS : STALE_PENDING_ANALYSIS_MS;
  return Number.isFinite(updatedAt) && now - updatedAt > staleAfter;
};

// Green bar fill for a generating JD, from the progress requisition_view sends; 0 means no bar.
export const getJdListProgress = (record) =>
  record?.jd_status !== JD_STATUSES.PENDING_ANALYSIS || isJdQueued(record) || isJdGenerationFailed(record)
    ? 0
    : Number(record?.generation_progress || 0);

// The indigo New pill (a brand-new v1.00 the AI wrote). Ant has no indigo preset, so, like the
// in-review pills below, it is applied as an inline style. Colours from the wireframe (.tag.new).
export const JD_NEW_STATUS_STYLE = {
  label: 'New',
  tooltip: TOOLTIP_STATUS_NEW,
  style: { color: '#3730a3', background: '#eef2ff', borderColor: '#c7d2fe' },
};

// The yellow Draft pill (a private draft). Colours from the wireframe (.tag.draft).
export const JD_DRAFT_STATUS_STYLE = {
  label: 'Draft',
  tooltip: TOOLTIP_STATUS_DRAFT,
  style: { color: '#8a5e0c', background: '#fff8e6', borderColor: '#ffd88a' },
};

// Base status badge display (label/color/tooltip, plus a style for New) shared by Jobs Library and
// Compensation Management. Jobs Library layers its own live-task-tracking
// override on top of this (e.g. "Processing Failed" while a JD is generating)
// -- a case Compensation Management's data never includes, since its backing
// query already excludes PENDING_ANALYSIS/GENERATION_FAILED rows.
export const getJdStatusBadge = (status) => {
  if (status === JD_STATUSES.NEW) return { ...JD_NEW_STATUS_STYLE, color: 'default' };
  if (status === JD_STATUSES.DRAFT) return { ...JD_DRAFT_STATUS_STYLE, color: 'default' };
  // Never a list row (only the latest version is listed), but never mislabelled "Unpublished" either:
  // a HISTORICAL version was never live
  if (status === JD_STATUSES.HISTORICAL) {
    return { label: 'Historical', color: 'default', tooltip: TOOLTIP_STATUS_HISTORICAL };
  }
  return {
    label: status === JD_STATUSES.PUBLISHED ? 'Published' : 'Unpublished',
    color: status === JD_STATUSES.PUBLISHED ? 'success' : 'processing',
    tooltip: getStatusTooltip(status),
  };
};

// Pills for the two in-review states. Colours match the JD status design doc.
// Ant's preset tag colours don't, so these are applied as an inline style.
export const JD_WORKFLOW_STATUS_STYLES = {
  [JD_STATUSES.PENDING_APPROVAL]: {
    label: 'Pending Approval',
    tooltip: TOOLTIP_STATUS_PENDING_APPROVAL,
    style: { color: '#16685f', background: 'rgba(22, 104, 95, 0.1)', borderColor: 'rgba(22, 104, 95, 0.35)' },
  },
  [JD_STATUSES.NEEDS_REVISION]: {
    label: 'Needs Revision',
    tooltip: TOOLTIP_STATUS_NEEDS_REVISION,
    style: { color: '#a2520a', background: 'rgba(162, 82, 10, 0.1)', borderColor: 'rgba(162, 82, 10, 0.35)' },
  },
};

// The in-review status of a list row, or null. requisition_view sends `family_status` (the Mongo
// approval doc's status, always current); `jd_status` is the latest BigQuery version's status and can
// lag, so it is only used for JDs with no approval doc.
export const getJdWorkflowStatus = (record) => {
  // The approval doc is the source of truth when there is one, even if it says the JD is not in review.
  const status = record?.family_status || record?.jd_status;
  return JD_WORKFLOW_STATUS_STYLES[status] ? status : null;
};

// One archived version archives the whole JD family, but requisition_view only drops the archived
// version itself, so an older version of an archived JD can still come back as a row. The approval
// doc's status is ARCHIVED for those; rows without a `family_status` (other views) are kept.
export const dropArchivedJdFamilies = (rows) =>
  Array.isArray(rows) ? rows.filter((row) => row?.family_status !== 'ARCHIVED') : rows;

// Status tag for Jobs Library and Admin All Jobs, with Processing / Queued / Processing Failed
// and the in-review states Pending Approval / Needs Revision.
// Jobs Library passes its own isFailed to hold off red while its live task is still running.
// ── Drafts are private (Save as Draft design, sections 4 and 6) ───────────────
// Who may see a JD's drafts: its owner, its delegate and the people its drafts are shared with (Ownership design,
// parts 2 and 3). Same rule as the backend's draft_visible_to.
const normEmail = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
export const canSeeJdDrafts = (ownerEmail, viewerEmail, delegateEmail = '', shareEmails = []) => {
  const viewer = normEmail(viewerEmail);
  return Boolean(viewer) && [ownerEmail, delegateEmail, ...(shareEmails || [])]
    .some((person) => normEmail(person) === viewer);
};

// A list row whose JD is in draft: the family status (MongoDB, sent to everyone) or, for the owner, the row itself
export const isJdInDraft = (record) =>
  record?.family_status === JD_STATUSES.DRAFT || record?.jd_status === JD_STATUSES.DRAFT;

// The Draft pill's tooltip (wireframe): the plain one for whoever may see the drafts, and for everyone else
// who is working on it. The Jobs Library lists are dashboards (each row is the JD's real latest version,
// the draft included), so the row does not carry the public version number the wireframe's text ends with
// ("(v…)"); opening the JD shows it in the info line.
export const getDraftPillTooltip = (record, viewerEmail) => {
  if (canSeeJdDrafts(record?.owner_email, viewerEmail, record?.delegate_email, record?.share_emails)) {
    return TOOLTIP_STATUS_DRAFT;
  }
  const owner = record?.current_owner || 'The owner';
  return `${owner} has a private draft. You see the latest non-draft version when you open it.`;
};

export const getJdStatusDisplay = (record, isFailed = isJdGenerationFailed(record), viewerEmail = undefined) => {
  const status = record?.jd_status;
  if (status === JD_STATUSES.PUBLISHED) return getJdStatusBadge(status);
  if (isFailed) return { label: 'Processing Failed', color: 'error', tooltip: TOOLTIP_STATUS_PROCESSING_FAILED };
  if (status === JD_STATUSES.PENDING_ANALYSIS) {
    return isJdQueued(record)
      ? { label: 'Queued', color: 'default', tooltip: TOOLTIP_STATUS_QUEUED }
      : { label: 'Processing', color: 'warning', tooltip: TOOLTIP_STATUS_PROCESSING };
  }
  const workflowStatus = getJdWorkflowStatus(record);
  if (workflowStatus) {
    const { label, tooltip, style } = JD_WORKFLOW_STATUS_STYLES[workflowStatus];
    return { label, color: 'default', tooltip, style };
  }
  // A JD in draft shows the yellow Draft pill to everyone (its state, never its content)
  if (isJdInDraft(record)) {
    return { ...JD_DRAFT_STATUS_STYLE, color: 'default', tooltip: getDraftPillTooltip(record, viewerEmail) };
  }
  return getJdStatusBadge(status);
};

// ── JD page: the draft bar / info line (wireframe "jd-alerts"; Save as Draft design, section 4) ──
// At most one line: for whoever may see the drafts, the private-draft bar on the latest draft (or the
// read-only line on an older one); for everyone else, while the JD is in draft, who is working on it.
// Returns { type: 'info' | 'warning', segments: [{ text, bold, lineBreak }] } or null (lineBreak: start a new line).
export const getDraftNotice = ({ familyStatus, viewedStatus, isLatest, canSeeDrafts, ownerName,
  publicVersion, latestVersion, isSharedWith = false, commentAuthors = [] }) => {
  if (!canSeeDrafts) {
    if (familyStatus !== JD_STATUSES.DRAFT) return null;
    return {
      type: 'warning',
      segments: [
        { text: ownerName || 'The owner', bold: true },
        { text: ` is working on a private draft of this JD. You're seeing the latest non-draft version (V${publicVersion}). Drafts become visible when the JD is sent for approval or published.` },
      ],
    };
  }
  if (viewedStatus !== JD_STATUSES.DRAFT) return null;
  // Someone the drafts are shared with (not the owner or delegate), on the latest draft: where to comment (wireframe)
  if (isSharedWith && isLatest) {
    return {
      type: 'info',
      segments: [
        { text: ownerName || 'The owner', bold: true },
        { text: ' shared this private draft with you. Click the ' },
        { text: 'Version Compare', bold: true },
        { text: ' button to open ' },
        { text: 'Draft Feedback', bold: true },
        { text: ' and comment on the changes.' },
      ],
    };
  }
  if (isLatest) {
    // the owner and delegate: who has given (still open) comments on it, on a line of its own
    const comments = commentAuthors?.length ? [
      { lineBreak: true, text: '' },
      { text: `${commentAuthors.join(', ')} has given comments on this draft`, bold: true },
      { text: ' – open the editor to view them.' },
    ] : [];
    return {
      type: 'info',
      segments: [
        { text: 'This draft is ' },
        { text: 'private', bold: true },
        { text: ` – only you and the people with access can see it. Everyone else sees V${publicVersion} with a Draft label until it is sent for approval or published.` },
        ...comments,
      ],
    };
  }
  return {
    type: 'info',
    segments: [{ text: `Previous private draft – read-only. Only the latest version (V${latestVersion}) can be edited.` }],
  };
};

// The two bar colours of the wireframe (.alert.info / .alert.warning)
export const DRAFT_NOTICE_STYLES = {
  info: { background: '#e6f0fe', borderColor: '#a9c7fa', color: '#163f8a' },
  warning: { background: '#fffaf1', borderColor: '#ffd88a', color: '#6e4a07' },
};

// ── Jobs Library "Current Version Info" (doc short spec; wireframe versionInfo) ─────────────────
export const CURRENT_VERSION_INFO_TITLE = 'Current Version Info';
// (the wireframe's text)
export const TOOLTIP_CURRENT_VERSION_INFO = 'Latest version, its date and the owner. Filter by a person to see every JD '
  + 'they own, are delegated or have a draft shared with them.';

// The cell's two lines: "v1.00 · Oct 14, 2026" (the full date and time on hover) and the owner, or
// Unclaimed for a JD nobody has claimed yet (owner_email "" on its approval record). The version and
// date are the row's: the JD's real latest version, a draft included, the same for everyone (the lists
// are dashboards; a draft's content is never on a row).
export const getCurrentVersionInfo = (record, viewerEmail = undefined) => {
  const ts = record?.last_updated_time_stamp;
  const when = ts !== undefined && ts !== null && ts !== '' ? new Date(Number.isFinite(Number(ts)) ? Number(ts) : ts) : null;
  const valid = when && !Number.isNaN(when.getTime());
  const day = valid ? when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const time = valid ? when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  const version = record?.version ? `v${record.version}` : '';
  // the time of the last edit before the date, e.g. "10:45AM on Nov 4, 2026" (hours and minutes, no seconds)
  const clock = valid
    ? `${when.getHours() % 12 || 12}:${String(when.getMinutes()).padStart(2, '0')}${when.getHours() < 12 ? 'AM' : 'PM'}`
    : '';
  return {
    line: [version, valid ? `${clock} on ${day}` : ''].filter(Boolean).join(' · ') || '—',
    hover: valid ? `Last modified ${day}, ${time}` : '',
    owner: record?.current_owner || '',
    unclaimed: record?.owner_email === '',
    // "(You)" after the owner, and the "Delegated to: X" line (shown to everyone) with "(You)" for the delegate
    ownerIsYou: Boolean(normEmail(viewerEmail)) && normEmail(record?.owner_email) === normEmail(viewerEmail),
    delegate: record?.delegate_email ? (record?.delegate_name || record.delegate_email) : '',
    delegateIsYou: Boolean(normEmail(viewerEmail)) && normEmail(record?.delegate_email) === normEmail(viewerEmail),
    // "Draft shared with you" (wireframe yourAccess): the viewer is shared with, and neither the owner nor the delegate
    sharedWithYou: Boolean(normEmail(viewerEmail))
      && (record?.share_emails || []).some((e) => normEmail(e) === normEmail(viewerEmail))
      && ![record?.owner_email, record?.delegate_email].some((e) => normEmail(e) === normEmail(viewerEmail)),
  };
};

// "Filter by person" on Current Version Info (wireframe): every owner, delegate and person a draft is shared with on
// the list, "(You)" after the viewer, plus Unclaimed. A row matches when the person owns it, is its delegate or has
// its drafts shared with them.
export const UNCLAIMED_FILTER_VALUE = '__unclaimed__';
export const getPersonFilters = (records, viewerEmail) => {
  const people = new Map();
  let unclaimed = false;
  (records || []).forEach((record) => {
    if (record?.owner_email === '') unclaimed = true;
    const shared = (record?.share_emails || []).map((e, i) => [e, record?.share_names?.[i]]);
    [[record?.owner_email, record?.current_owner], [record?.delegate_email, record?.delegate_name], ...shared]
      .forEach(([email, name]) => {
        const key = normEmail(email);
        if (key && !people.has(key)) people.set(key, name || email);
      });
  });
  const you = normEmail(viewerEmail);
  // the viewer first ("… (You)"), then everyone else alphabetically
  const filters = [...people.entries()]
    .map(([value, name]) => ({ value, text: value === you ? `${name} (You)` : name }))
    .sort((a, b) => Number(b.value === you) - Number(a.value === you) || a.text.localeCompare(b.text));
  return unclaimed ? [...filters, { value: UNCLAIMED_FILTER_VALUE, text: 'Unclaimed' }] : filters;
};
export const matchesPersonFilter = (value, record) => (value === UNCLAIMED_FILTER_VALUE
  ? record?.owner_email === ''
  : [record?.owner_email, record?.delegate_email, ...(record?.share_emails || [])]
    .some((email) => normEmail(email) === value));

// Status column filter (Ant Table `filters`/`onFilter`) shared by Jobs Library
// and Compensation Management: "Published" and "New" match PUBLISHED and NEW exactly;
// "Unpublished" buckets every other jd_status together (GENERATED,
// PENDING_APPROVAL, UNPUBLISHED, HUMAN-EDITED, etc.), as getJdStatusBadge labels them.
export const JD_STATUS_FILTERS = [
  { text: 'Published', value: JD_STATUSES.PUBLISHED },
  { text: 'New', value: JD_STATUSES.NEW },
  { text: 'Unpublished', value: JD_STATUSES.UNPUBLISHED },
];

export const jdStatusOnFilter = (value, record) => {
  if (value === JD_STATUSES.PUBLISHED || value === JD_STATUSES.NEW) return record.jd_status === value;
  return record.jd_status !== JD_STATUSES.PUBLISHED && record.jd_status !== JD_STATUSES.NEW;
};

// Status column filter for Jobs Library and Admin All Jobs: one option per status tag
// (the 9 labels getJdStatusDisplay can return), in lifecycle order.
export const JD_LIST_STATUS_FILTERS = [
  'Queued',
  'Processing',
  'Processing Failed',
  'New',
  'Draft',
  'Unpublished',
  'Pending Approval',
  'Needs Revision',
  'Published',
].map((label) => ({ text: label, value: label }));

// Matches on the label the row's status tag shows, so each row falls under exactly one option.
export const jdListStatusOnFilter = (value, record) => getJdStatusDisplay(record).label === value;

// Is this list row the JD the Jobs Library is tracking a task for (create, revise or retry)? A row
// matches the task's id, its job, or the version the task's result was saved in: a Revise that
// replaced the latest AI-generated draft keeps that draft's id (jobTaskId.sb_unique_id, from the
// backend's revise_jd_saved event), and the task's own id then no longer exists.
export const isJdTaskRow = (jobTaskId, record) =>
  Boolean(jobTaskId?.job_id && jobTaskId?.task_id) && (
    jobTaskId.job_id === record?.job_id ||
    jobTaskId.task_id === record?.sb_unique_id ||
    (Boolean(jobTaskId.sb_unique_id) && jobTaskId.sb_unique_id === record?.sb_unique_id)
  );

// The task-stream event in which the backend names the version that holds a Revise result.
export const REVISE_RESULT_EVENT = 'revise_jd_saved';

// The jobTaskId to track once that event arrives (the stream it came on, task_id, is kept).
export const withReviseResult = (jobTaskId, event) => {
  const savedId = event?.extra_info?.sb_unique_id;
  if (event?.event !== REVISE_RESULT_EVENT || !savedId || !jobTaskId) return jobTaskId;
  return { ...jobTaskId, sb_unique_id: savedId };
};

// ── JD Tabs ───────────────────────────────────────────────────────────────────

export const JD_TABS = {
  JOB_DESCRIPTION: 'job-description',
  JOB_COMPARE:     'job-compare',
};

export const JD_TAB_LABELS = {
  [JD_TABS.JOB_DESCRIPTION]: 'Job Description',
  [JD_TABS.JOB_COMPARE]:     'Compare Published JDs',
};

// ── Human Editor Block Types ───────────────────────────────────────────────────

export const BLOCK_TYPES = {
  LOCKED:     'LOCKED',
  NEEDS_WORK: 'NEEDS_WORK',
  GHOST:      'GHOST',
  MODIFIED:   'MODIFIED',
};

// ── Compare View ──────────────────────────────────────────────────────────────

export const COMPARE_VIEW = {
  DIFFERENCE: 'difference',
  SUMMARY:    'summary',
};

export const COMPARE_VIEW_LABELS = {
  [COMPARE_VIEW.DIFFERENCE]: 'Side-by-side view',
  [COMPARE_VIEW.SUMMARY]:    'AI Comparison Summary',
};

export const COMPARE_ENTRY = {
  JOBS_LIBRARY: 'jobs-library',
  JOB_DETAILS:  'job-details',
};

export const COMPARE_LOADING = {
  AI:      'ai',
  SUMMARY: 'summary',
};

// ── Diff Checker ──────────────────────────────────────────────────────────────

export const VIEW_MODE = {
  LOADING:  'LOADING',
  APPROVER: 'APPROVER',
  HISTORY:  'HISTORY',
  FEEDBACK: 'FEEDBACK',   // Draft Feedback: the person a private draft is shared with (Ownership design, part 3)
};

export const WORKFLOW_STATUS = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  LOADING_STATUS:   'LOADING_STATUS',
};

export const DECISION = {
  APPROVE:  'APPROVE',
  REVIEW:   'REVIEW',
  REMOVED:  'REMOVED',
  MODIFIED: 'MODIFIED',
  UNCHANGED:'UNCHANGED'
};

export const STATUS_CLASS_MAP = {
  added:       'diff-added',
  removed:     'diff-removed',
  modified:    'diff-modified',
  ghost_alert: 'diff-ghost-alert',
};


// ── Feedback ──────────────────────────────────────────────────────────────────

export const FEEDBACK_TYPE = {
    UP: 'WORKS',
    DOWN: 'HAS_ISSUES',
};

// ── JD Creation ───────────────────────────────────────────────────────────────

export const JD_ACTION = { CREATE: 'create', REVISE: 'revise', UPLOAD: 'upload' };

export const FIELD_NAME_MAP = {
  jobCode:            'job_code',
  roleTitle:          'role',
  altRoleTitle:       'alt_role_title',
  jobTitleForJd:      'job_title_for_jd',
  legalEntity:        'legal_entity',
  businessUnit:       'business_unit',
  functionDepartment: 'function',
  subFunction:        'sub_function',
  reportingTo:        'reporting_to',
  location:           'location',
  jobGrade:           'level',
  roleType:           'role_type',
  workEnvironment:    'work_environment',
  roleContext:        'role_context',
};

export const FIELD_DB_MAP = {
  legalEntity:        'legal_entity',
  businessUnit:       'business_unit',
  functionDepartment: 'function',
  jobGrade:           'level',
  roleType:           'role_type',
  subFunction:        'sub_function',
  location:           'location',
  workEnvironment:    'work_environment',
};
